import React, { useState, useEffect } from 'react';
import { inventoryApi, roomsApi } from '../../services/api';
import { fmt } from '../../utils/billing';
import { useConfirm, useNotify } from '../../context/ConfirmContext';

export default function InventoryPage({ hotel, onSuccess }) {
  const confirm = useConfirm();
  const notify = useNotify();
  const [activeTab, setActiveTab] = useState('items'); // 'items' | 'categories' | 'logs'
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [logs, setLogs] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'guest' | 'room'

  // Modals
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({
    name: '', categoryId: '', unit: 'pcs', quantity: '0', minStockAlert: '5', costPerUnit: '0', sellPrice: '', location: '', notes: '', itemType: 'guest'
  });

  const [showCatModal, setShowCatModal] = useState(false);
  const [catForm, setCatForm] = useState({ name: '', description: '' });
  const [inlineNewCategoryName, setInlineNewCategoryName] = useState('');
  const [savingInlineCategory, setSavingInlineCategory] = useState(false);

  const [showLogModal, setShowLogModal] = useState(false);
  const [logForm, setLogForm] = useState({
    itemId: '', type: 'out', quantity: '1', roomNumber: '', note: ''
  });

  useEffect(() => {
    if (hotel?.id) {
      loadAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotel?.id]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [catsRes, itemsRes, logsRes, roomsRes] = await Promise.all([
        inventoryApi.categories(hotel.id),
        inventoryApi.items(hotel.id),
        inventoryApi.logs(hotel.id),
        roomsApi.list(hotel.id)
      ]);
      setCategories(catsRes || []);
      setItems(itemsRes || []);
      setLogs(logsRes || []);
      setRooms(roomsRes || []);
    } catch (err) {
      console.error('Error loading inventory data:', err);
    }
    setLoading(false);
  };

  // Metrics
  const totalItemsCount = items.length;
  const lowStockCount = items.filter(i => i.stockStatus === 'low_stock' || i.stockStatus === 'out_of_stock').length;
  const totalStockValuation = items.reduce((sum, i) => sum + (Number(i.quantity || 0) * Number(i.cost_per_unit || 0)), 0);
  const totalLogsToday = logs.filter(l => {
    if (!l.created_at) return false;
    const d = new Date(l.created_at).toLocaleDateString('en-CA');
    const today = new Date().toLocaleDateString('en-CA');
    return d === today;
  }).length;

  // Filtered Items
  const filteredItems = items.filter(i => {
    const matchesSearch = i.name.toLowerCase().includes(search.toLowerCase()) ||
                          (i.location && i.location.toLowerCase().includes(search.toLowerCase()));
    const matchesCat = categoryFilter === 'all' || i.category_id === categoryFilter;
    const matchesType = typeFilter === 'all' || (i.item_type || 'guest') === typeFilter;
    return matchesSearch && matchesCat && matchesType;
  });

  // Handlers — Item Modal
  const openAddItemModal = () => {
    setEditingItem(null);
    setItemForm({
      name: '', categoryId: categories[0]?.id || '', unit: 'pcs', quantity: '0', minStockAlert: '5', costPerUnit: '0', sellPrice: '', location: '', notes: '', itemType: 'guest'
    });
    setShowItemModal(true);
  };

  const openEditItemModal = (item) => {
    setEditingItem(item);
    setItemForm({
      name: item.name || '',
      categoryId: item.category_id || '',
      unit: item.unit || 'pcs',
      quantity: String(item.quantity || 0),
      minStockAlert: String(item.min_stock_alert || 5),
      costPerUnit: String(item.cost_per_unit || 0),
      sellPrice: item.sell_price !== null && item.sell_price !== undefined ? String(item.sell_price) : '',
      location: item.location || '',
      notes: item.notes || '',
      itemType: item.item_type || item.itemType || 'guest'
    });
    setShowItemModal(true);
  };

  const handleSaveItem = async (e) => {
    e.preventDefault();
    if (!itemForm.name.trim()) return await notify('Item name is required');
    try {
      if (editingItem) {
        await inventoryApi.updateItem(editingItem.id, {
          ...itemForm,
          hotelId: hotel.id
        });
        onSuccess?.('Inventory item updated successfully');
      } else {
        await inventoryApi.createItem({
          ...itemForm,
          hotelId: hotel.id
        });
        onSuccess?.('New inventory item added successfully');
      }
      setShowItemModal(false);
      loadAll();
    } catch (err) {
      await notify('Error saving item: ' + (err.message || 'Server error'));
    }
  };

  const ITEM_TYPE_LABELS = { guest: 'Guest Consumable', room: 'Room Supply', linen: 'Linen (goes to laundry)' };
  const ITEM_TYPE_CYCLE = { guest: 'room', room: 'linen', linen: 'guest' };

  const handleToggleItemType = async (item) => {
    const current = item.item_type || item.itemType || 'guest';
    const newType = ITEM_TYPE_CYCLE[current] || 'guest';
    try {
      await inventoryApi.updateItem(item.id, {
        itemType: newType
      });
      onSuccess?.(`Updated "${item.name}" to ${ITEM_TYPE_LABELS[newType]}`);
      loadAll();
    } catch (err) {
      await notify('Error updating item type: ' + err.message);
    }
  };

  const handleDeleteItem = async (id, name) => {
    if (!await confirm({ icon: '🗑️', title: 'Delete Item', body: `Are you sure you want to delete "${name}" from inventory?`, confirmText: 'Delete', danger: true })) return;
    try {
      await inventoryApi.deleteItem(id);
      onSuccess?.('Item removed from inventory');
      loadAll();
    } catch (err) {
      await notify('Error deleting item: ' + err.message);
    }
  };

  // Handlers — Category Modal
  const handleSaveCategory = async (e) => {
    e.preventDefault();
    if (!catForm.name.trim()) return await notify('Category name is required');
    try {
      await inventoryApi.createCategory({
        hotelId: hotel.id,
        name: catForm.name.trim(),
        description: catForm.description.trim()
      });
      onSuccess?.('Category added successfully');
      setShowCatModal(false);
      setCatForm({ name: '', description: '' });
      loadAll();
    } catch (err) {
      await notify('Error creating category: ' + err.message);
    }
  };

  // Quick-add a category from within the Add/Edit Item modal, so staff don't have
  // to abandon an in-progress item just to go create a category first — the item
  // form's Category dropdown otherwise has nothing to select for a hotel with no
  // categories yet (which is the common case for a newly set-up property).
  const handleInlineCreateCategory = async () => {
    const name = inlineNewCategoryName.trim();
    if (!name) return;
    setSavingInlineCategory(true);
    try {
      const created = await inventoryApi.createCategory({ hotelId: hotel.id, name });
      setCategories(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setItemForm(f => ({ ...f, categoryId: created.id }));
      setInlineNewCategoryName('');
      onSuccess?.('Category added');
    } catch (err) {
      await notify('Error creating category: ' + err.message);
    }
    setSavingInlineCategory(false);
  };

  const handleDeleteCategory = async (id, name) => {
    if (!await confirm({ icon: '🗑️', title: 'Delete Category', body: `Delete category "${name}"?`, confirmText: 'Delete', danger: true })) return;
    try {
      await inventoryApi.deleteCategory(id);
      onSuccess?.('Category deleted');
      loadAll();
    } catch (err) {
      await notify('Error deleting category: ' + err.message);
    }
  };

  // Handlers — Quick Stock Movement Log Modal
  const openLogModal = (item, defaultType = 'out') => {
    setLogForm({
      itemId: item ? item.id : (items[0]?.id || ''),
      type: defaultType,
      quantity: '1',
      roomNumber: '',
      note: ''
    });
    setShowLogModal(true);
  };

  const handleSaveLog = async (e) => {
    e.preventDefault();
    if (!logForm.itemId) return await notify('Select an inventory item');
    if (!logForm.quantity || Number(logForm.quantity) <= 0) return await notify('Quantity must be greater than 0');

    try {
      await inventoryApi.createLog({
        hotelId: hotel.id,
        ...logForm
      });
      onSuccess?.('Stock movement recorded successfully');
      setShowLogModal(false);
      loadAll();
    } catch (err) {
      await notify('Error recording stock movement: ' + err.message);
    }
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)', margin: 0 }}>📦 Inventory & Supplies</h1>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
            Track housekeeping linen, room amenities, F&B minibar stock, and low-stock alerts.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-ghost" style={{ fontWeight: 700 }} onClick={() => setShowCatModal(true)}>
            + Add Category
          </button>
          <button className="btn btn-ghost" style={{ fontWeight: 700, border: '1px solid var(--accent)', color: 'var(--accent)' }} onClick={() => openLogModal(null, 'in')}>
            📥 Record Stock In
          </button>
          <button className="btn btn-gold" style={{ fontWeight: 800 }} onClick={openAddItemModal}>
            + Add New Item
          </button>
        </div>
      </div>

      {/* Metrics Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px', boxShadow: '0 2px 6px rgba(0,0,0,0.03)' }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>TOTAL ITEMS</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: 'var(--text)', marginTop: 6 }}>{totalItemsCount}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Catalogued in inventory</div>
        </div>

        <div style={{ background: lowStockCount > 0 ? 'rgba(231,76,60,0.06)' : 'white', border: lowStockCount > 0 ? '1px solid rgba(231,76,60,0.3)' : '1px solid var(--border)', borderRadius: 14, padding: '18px 20px', boxShadow: '0 2px 6px rgba(0,0,0,0.03)' }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: lowStockCount > 0 ? 'var(--danger)' : 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>LOW STOCK ALERTS</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: lowStockCount > 0 ? 'var(--danger)' : 'var(--text)', marginTop: 6 }}>{lowStockCount}</div>
          <div style={{ fontSize: 11, color: lowStockCount > 0 ? 'var(--danger)' : 'var(--muted)', marginTop: 4 }}>
            {lowStockCount > 0 ? '⚠️ Action required: restock soon' : 'All items sufficiently stocked'}
          </div>
        </div>

        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px', boxShadow: '0 2px 6px rgba(0,0,0,0.03)' }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>TOTAL STOCK VALUE</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: 'var(--accent)', marginTop: 6 }}>{fmt(totalStockValuation)}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Est. total value on hand</div>
        </div>

        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px', boxShadow: '0 2px 6px rgba(0,0,0,0.03)' }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>TODAY'S MOVEMENTS</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: 'var(--text)', marginTop: 6 }}>{totalLogsToday}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Stock adjustments today</div>
        </div>
      </div>

      {/* Tabs Bar */}
      <div style={{ display: 'flex', borderBottom: '2px solid var(--border)', marginBottom: 20 }}>
        <button
          onClick={() => setActiveTab('items')}
          style={{
            padding: '10px 20px', fontWeight: 700, fontSize: 13, background: 'none', border: 'none',
            color: activeTab === 'items' ? 'var(--accent)' : 'var(--muted)',
            borderBottom: activeTab === 'items' ? '3px solid var(--accent)' : '3px solid transparent',
            cursor: 'pointer', marginBottom: -2
          }}
        >
          📦 Stock Items ({items.length})
        </button>

        <button
          onClick={() => setActiveTab('categories')}
          style={{
            padding: '10px 20px', fontWeight: 700, fontSize: 13, background: 'none', border: 'none',
            color: activeTab === 'categories' ? 'var(--accent)' : 'var(--muted)',
            borderBottom: activeTab === 'categories' ? '3px solid var(--accent)' : '3px solid transparent',
            cursor: 'pointer', marginBottom: -2
          }}
        >
          🏷️ Categories ({categories.length})
        </button>

        <button
          onClick={() => setActiveTab('logs')}
          style={{
            padding: '10px 20px', fontWeight: 700, fontSize: 13, background: 'none', border: 'none',
            color: activeTab === 'logs' ? 'var(--accent)' : 'var(--muted)',
            borderBottom: activeTab === 'logs' ? '3px solid var(--accent)' : '3px solid transparent',
            cursor: 'pointer', marginBottom: -2
          }}
        >
          📜 Movement Audit Log ({logs.length})
        </button>
      </div>

      {/* TAB 1: STOCK ITEMS */}
      {activeTab === 'items' && (
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
          {/* Controls */}
          <div style={{ padding: '16px 20px', background: 'var(--s1)', borderBottom: '1px solid var(--border)', display: 'flex', gap: 14, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search items by name or location..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ flex: 1, padding: '9px 14px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}
            />
            <select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, background: 'white' }}
            >
              <option value="all">All Categories ({items.length})</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
              style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, background: 'white' }}
            >
              <option value="all">All Usage Types ({items.length})</option>
              <option value="guest">🥤 Guest Consumables ({items.filter(i => (i.item_type || 'guest') === 'guest').length})</option>
              <option value="room">🛏️ Room Supplies ({items.filter(i => (i.item_type || 'guest') === 'room').length})</option>
              <option value="linen">🧺 Linen ({items.filter(i => (i.item_type || 'guest') === 'linen').length})</option>
            </select>
          </div>

          {/* Table */}
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Loading inventory items...</div>
          ) : filteredItems.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>No inventory items found. Click "+ Add New Item" to create one.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, textAlign: 'left' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid var(--border)', color: 'var(--muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <th style={{ padding: '12px 16px' }}>Item Name</th>
                  <th style={{ padding: '12px 16px' }}>Category</th>
                  <th style={{ padding: '12px 16px' }}>Usage Type</th>
                  <th style={{ padding: '12px 16px' }}>Current Stock</th>
                  <th style={{ padding: '12px 16px' }}>Status</th>
                  <th style={{ padding: '12px 16px' }}>Cost / Unit</th>
                  <th style={{ padding: '12px 16px' }}>Stock Value</th>
                  <th style={{ padding: '12px 16px' }}>Location</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map(item => {
                  const qty = Number(item.quantity) || 0;
                  const cost = Number(item.cost_per_unit) || 0;
                  const val = qty * cost;

                  return (
                    <tr key={item.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '14px 16px', fontWeight: 700, color: 'var(--text)' }}>
                        {item.name}
                        {item.notes && <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 400 }}>{item.notes}</div>}
                      </td>
                      <td style={{ padding: '14px 16px', color: 'var(--muted)' }}>{item.category_name || '—'}</td>
                      <td style={{ padding: '14px 16px' }}>
                        <button
                          type="button"
                          onClick={() => handleToggleItemType(item)}
                          title="Click to cycle between Guest Consumable, Room Supply, and Linen"
                          style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
                        >
                          {item.item_type === 'room' ? (
                            <span style={{ padding: '4px 10px', borderRadius: 8, background: 'rgba(52,152,219,0.12)', color: '#2980b9', fontSize: 11, fontWeight: 700, border: '1px solid rgba(52,152,219,0.3)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              🛏️ Room Supply ⚡
                            </span>
                          ) : item.item_type === 'linen' ? (
                            <span style={{ padding: '4px 10px', borderRadius: 8, background: 'rgba(22,160,133,0.12)', color: '#16a085', fontSize: 11, fontWeight: 700, border: '1px solid rgba(22,160,133,0.3)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              🧺 Linen ⚡
                            </span>
                          ) : (
                            <span style={{ padding: '4px 10px', borderRadius: 8, background: 'rgba(155,89,182,0.12)', color: '#8e44ad', fontSize: 11, fontWeight: 700, border: '1px solid rgba(155,89,182,0.3)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              🥤 Guest Consumable ⚡
                            </span>
                          )}
                        </button>
                      </td>
                      <td style={{ padding: '14px 16px', fontWeight: 800, fontSize: 14 }}>
                        {qty} <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 500 }}>{item.unit}</span>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        {item.stockStatus === 'out_of_stock' && (
                          <span style={{ padding: '3px 8px', borderRadius: 6, background: 'rgba(231,76,60,0.12)', color: 'var(--danger)', fontSize: 11, fontWeight: 800 }}>
                            OUT OF STOCK
                          </span>
                        )}
                        {item.stockStatus === 'low_stock' && (
                          <span style={{ padding: '3px 8px', borderRadius: 6, background: 'rgba(230,126,34,0.12)', color: '#d35400', fontSize: 11, fontWeight: 800 }}>
                            LOW STOCK ({item.min_stock_alert} alert)
                          </span>
                        )}
                        {item.stockStatus === 'in_stock' && (
                          <span style={{ padding: '3px 8px', borderRadius: 6, background: 'rgba(46,204,113,0.12)', color: '#27ae60', fontSize: 11, fontWeight: 800 }}>
                            IN STOCK
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '14px 16px' }}>{fmt(cost)}</td>
                      <td style={{ padding: '14px 16px', fontWeight: 700, color: 'var(--accent)' }}>{fmt(val)}</td>
                      <td style={{ padding: '14px 16px', color: 'var(--muted)' }}>{item.location || '—'}</td>
                      <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button className="btn btn-sm btn-ghost" title="Stock In" onClick={() => openLogModal(item, 'in')}>+ Stock In</button>
                          <button className="btn btn-sm btn-ghost" title="Issue / Stock Out" onClick={() => openLogModal(item, 'out')}>- Issue</button>
                          <button className="btn btn-sm btn-ghost" onClick={() => openEditItemModal(item)}>Edit</button>
                          <button className="btn btn-sm btn-ghost" style={{ color: 'var(--danger)' }} onClick={() => handleDeleteItem(item.id, item.name)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* TAB 2: CATEGORIES */}
      {activeTab === 'categories' && (
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', background: 'var(--s1)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--muted)' }}>Inventory Categories ({categories.length})</div>
            <button className="btn btn-sm btn-gold" onClick={() => setShowCatModal(true)}>+ Add Category</button>
          </div>
          {categories.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>No categories yet. Click "+ Add Category" to organize items.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, textAlign: 'left' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid var(--border)', color: 'var(--muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <th style={{ padding: '12px 16px' }}>Category Name</th>
                  <th style={{ padding: '12px 16px' }}>Description</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map(cat => (
                  <tr key={cat.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '14px 16px', fontWeight: 700 }}>{cat.name}</td>
                    <td style={{ padding: '14px 16px', color: 'var(--muted)' }}>{cat.description || '—'}</td>
                    <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                      <button className="btn btn-sm btn-ghost" style={{ color: 'var(--danger)' }} onClick={() => handleDeleteCategory(cat.id, cat.name)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* TAB 3: MOVEMENT AUDIT LOG */}
      {activeTab === 'logs' && (
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', background: 'var(--s1)', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--muted)' }}>Stock Movement History (Last 100 Entries)</div>
          </div>
          {logs.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>No stock movement logs recorded yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, textAlign: 'left' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid var(--border)', color: 'var(--muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <th style={{ padding: '12px 16px' }}>Date & Time</th>
                  <th style={{ padding: '12px 16px' }}>Item</th>
                  <th style={{ padding: '12px 16px' }}>Type</th>
                  <th style={{ padding: '12px 16px' }}>Quantity</th>
                  <th style={{ padding: '12px 16px' }}>Room #</th>
                  <th style={{ padding: '12px 16px' }}>Note / Reason</th>
                  <th style={{ padding: '12px 16px' }}>Logged By</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '12px 16px', color: 'var(--muted)' }}>
                      {new Date(log.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td style={{ padding: '12px 16px', fontWeight: 700 }}>{log.item_name || 'Item'}</td>
                    <td style={{ padding: '12px 16px' }}>
                      {log.type === 'in' && <span style={{ padding: '2px 6px', borderRadius: 4, background: 'rgba(46,204,113,0.12)', color: '#27ae60', fontWeight: 800, fontSize: 10 }}>STOCK IN (+)</span>}
                      {log.type === 'out' && <span style={{ padding: '2px 6px', borderRadius: 4, background: 'rgba(52,152,219,0.12)', color: '#2980b9', fontWeight: 800, fontSize: 10 }}>ISSUED (-)</span>}
                      {log.type === 'waste' && <span style={{ padding: '2px 6px', borderRadius: 4, background: 'rgba(231,76,60,0.12)', color: '#c0392b', fontWeight: 800, fontSize: 10 }}>DISCARDED / DAMAGE (-)</span>}
                    </td>
                    <td style={{ padding: '12px 16px', fontWeight: 800 }}>{log.quantity}</td>
                    <td style={{ padding: '12px 16px' }}>{log.room_number ? `Room ${log.room_number}` : '—'}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--muted)' }}>{log.note || '—'}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--muted)' }}>{log.logged_by_name || 'Staff'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* MODAL: ADD / EDIT ITEM */}
      {showItemModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 520, padding: 24, boxShadow: '0 8px 30px rgba(0,0,0,0.2)' }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, marginBottom: 16 }}>{editingItem ? 'Edit Inventory Item' : 'Add New Inventory Item'}</h2>
            <form onSubmit={handleSaveItem}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>Item Name *</label>
                <input required style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, marginTop: 4 }} value={itemForm.name} onChange={e => setItemForm({ ...itemForm, name: e.target.value })} placeholder="e.g. Water Bottle 1L or White Bed Sheet" />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>Consumable Usage Type *</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 6 }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', border: `1.5px solid ${itemForm.itemType === 'guest' ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 10, background: itemForm.itemType === 'guest' ? 'rgba(180,83,9,0.05)' : 'white', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                    <input type="radio" name="itemType" value="guest" checked={itemForm.itemType === 'guest'} onChange={e => setItemForm({ ...itemForm, itemType: e.target.value })} style={{ marginTop: 2 }} />
                    <span>🥤 Guest Consumable<br/><span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>Water, drinks, food — Shown in Room Billing</span></span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', border: `1.5px solid ${itemForm.itemType === 'room' ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 10, background: itemForm.itemType === 'room' ? 'rgba(180,83,9,0.05)' : 'white', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                    <input type="radio" name="itemType" value="room" checked={itemForm.itemType === 'room'} onChange={e => setItemForm({ ...itemForm, itemType: e.target.value })} style={{ marginTop: 2 }} />
                    <span>🛏️ Room / Housekeeping<br/><span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>Toiletries, stationery — Internal stock only</span></span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', border: `1.5px solid ${itemForm.itemType === 'linen' ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 10, background: itemForm.itemType === 'linen' ? 'rgba(180,83,9,0.05)' : 'white', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                    <input type="radio" name="itemType" value="linen" checked={itemForm.itemType === 'linen'} onChange={e => setItemForm({ ...itemForm, itemType: e.target.value })} style={{ marginTop: 2 }} />
                    <span>🧺 Linen<br/><span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>Bed sheets, towels, pillow covers — Tracked in Laundry</span></span>
                  </label>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>Category</label>
                  <select style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, marginTop: 4, background: 'white' }} value={itemForm.categoryId} onChange={e => setItemForm({ ...itemForm, categoryId: e.target.value })}>
                    <option value="">{categories.length === 0 ? '-- No categories yet --' : '-- Select Category --'}</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    <input
                      style={{ flex: 1, padding: '7px 10px', borderRadius: 7, border: '1px solid var(--border)', fontSize: 12 }}
                      value={inlineNewCategoryName}
                      onChange={e => setInlineNewCategoryName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleInlineCreateCategory(); } }}
                      placeholder="+ New category name"
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost"
                      disabled={!inlineNewCategoryName.trim() || savingInlineCategory}
                      onClick={handleInlineCreateCategory}
                    >
                      {savingInlineCategory ? '…' : 'Add'}
                    </button>
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>Unit</label>
                  <input style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, marginTop: 4 }} value={itemForm.unit} onChange={e => setItemForm({ ...itemForm, unit: e.target.value })} placeholder="pcs, kg, box, bottle" />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>Quantity</label>
                  <input type="number" min="0" style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, marginTop: 4 }} value={itemForm.quantity} onChange={e => setItemForm({ ...itemForm, quantity: e.target.value })} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>Low Stock Alert</label>
                  <input type="number" min="0" style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, marginTop: 4 }} value={itemForm.minStockAlert} onChange={e => setItemForm({ ...itemForm, minStockAlert: e.target.value })} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>Cost / Unit (₹)</label>
                  <input type="number" min="0" step="any" style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, marginTop: 4 }} value={itemForm.costPerUnit} onChange={e => setItemForm({ ...itemForm, costPerUnit: e.target.value })} />
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>Guest Sell Price (₹) — optional</label>
                <input type="number" min="0" step="any" style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, marginTop: 4 }} value={itemForm.sellPrice} onChange={e => setItemForm({ ...itemForm, sellPrice: e.target.value })} placeholder="Leave blank to type it manually each time" />
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>If set, this auto-fills the Unit Price when a staff member adds this item as a room service at checkout — keeps pricing consistent across shifts.</div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>Storage Location</label>
                <input style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, marginTop: 4 }} value={itemForm.location} onChange={e => setItemForm({ ...itemForm, location: e.target.value })} placeholder="e.g. Linen Room - Shelf 2" />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>Notes / Specifications</label>
                <textarea style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, marginTop: 4 }} value={itemForm.notes} onChange={e => setItemForm({ ...itemForm, notes: e.target.value })} placeholder="Additional notes..." />
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-ghost" onClick={() => setShowItemModal(false)}>Cancel</button>
                <button className="btn btn-gold" style={{ fontWeight: 800 }}>Save Item ✓</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: ADD CATEGORY */}
      {showCatModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 420, padding: 24, boxShadow: '0 8px 30px rgba(0,0,0,0.2)' }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, marginBottom: 16 }}>Add Inventory Category</h2>
            <form onSubmit={handleSaveCategory}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>Category Name *</label>
                <input required style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, marginTop: 4 }} value={catForm.name} onChange={e => setCatForm({ ...catForm, name: e.target.value })} placeholder="e.g. Linen & Laundry" />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>Description</label>
                <textarea style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, marginTop: 4 }} value={catForm.description} onChange={e => setCatForm({ ...catForm, description: e.target.value })} placeholder="Category description..." />
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-ghost" onClick={() => setShowCatModal(false)}>Cancel</button>
                <button className="btn btn-gold" style={{ fontWeight: 800 }}>Save Category ✓</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: STOCK MOVEMENT LOG */}
      {showLogModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 460, padding: 24, boxShadow: '0 8px 30px rgba(0,0,0,0.2)' }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, marginBottom: 16 }}>Record Stock Movement</h2>
            <form onSubmit={handleSaveLog}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>Inventory Item *</label>
                <select style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, marginTop: 4, background: 'white' }} value={logForm.itemId} onChange={e => setLogForm({ ...logForm, itemId: e.target.value })}>
                  {items.map(i => <option key={i.id} value={i.id}>{i.name} (Current Stock: {i.quantity} {i.unit})</option>)}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>Action Type</label>
                  <select style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, marginTop: 4, background: 'white' }} value={logForm.type} onChange={e => setLogForm({ ...logForm, type: e.target.value })}>
                    <option value="in">📥 Stock In (+)</option>
                    <option value="out">📤 Issue to Room / Housekeeping (-)</option>
                    <option value="waste">🗑️ Damaged / Waste (-)</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>Quantity</label>
                  <input type="number" min="1" required style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, marginTop: 4 }} value={logForm.quantity} onChange={e => setLogForm({ ...logForm, quantity: e.target.value })} />
                </div>
              </div>

              {logForm.type === 'out' && (
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>Target Room (Optional)</label>
                  <select style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, marginTop: 4, background: 'white' }} value={logForm.roomNumber} onChange={e => setLogForm({ ...logForm, roomNumber: e.target.value })}>
                    <option value="">-- General Stock Out --</option>
                    {rooms.map(r => <option key={r.id} value={r.number}>Room {r.number}</option>)}
                  </select>
                </div>
              )}

              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>Note / Reason</label>
                <textarea style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, marginTop: 4 }} value={logForm.note} onChange={e => setLogForm({ ...logForm, note: e.target.value })} placeholder="e.g. Restocked by supplier / Replaced torn towel..." />
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-ghost" onClick={() => setShowLogModal(false)}>Cancel</button>
                <button className="btn btn-gold" style={{ fontWeight: 800 }}>Save Movement ✓</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
